import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"

/**
 * Field-level preview of every attribute the override is about to write, on
 * every Thing it touches. Raw attribute names on purpose: this is a super-admin
 * escape hatch, and paraphrasing the write is how an operator approves
 * something other than what lands in DynamoDB.
 */
export const SaLockerMigrationConfirmDialog: FC<{
  open: boolean
  onClose: () => void
  onConfirm: () => void
  isSaving: boolean
  rows: { id: string; changes: string[] }[]
}> = ({ open, onClose, onConfirm, isSaving, rows }) => {
  const { t } = useTranslation()
  return (
    <Dialog data-ui="UI-SL2W-153" open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("lockerMigrationOverride.confirmTitle")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("lockerMigrationOverride.confirmBody")}
        </Typography>
        <Stack direction="column" spacing={1.5}>
          {rows.map((row) => (
            <Box key={row.id}>
              <Typography variant="caption" fontFamily="monospace" fontWeight={600}>
                {row.id}
              </Typography>
              {row.changes.length === 0 ? (
                <Typography variant="caption" display="block" color="text.secondary">
                  {t("lockerMigrationOverride.noFlagChange")}
                </Typography>
              ) : (
                row.changes.map((c) => (
                  <Typography
                    key={c}
                    variant="caption"
                    display="block"
                    fontFamily="monospace"
                  >
                    {c}
                  </Typography>
                ))
              )}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSaving} sx={{ textTransform: "none" }}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={onConfirm}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: "none" }}
        >
          {t("lockerMigrationOverride.apply")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SaLockerMigrationConfirmDialog
